import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { logClientAudit } from '../lib/audit'
import type { Camera } from '../types'

export function useCameras(siteId: string | undefined) {
  const [cameras, setCameras] = useState<Camera[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!siteId) { setCameras([]); setLoading(false); return }
    setLoading(true)
    const { data } = await supabase
      .from('cameras')
      .select('*')
      .eq('site_id', siteId)
      .order('cam_number')
    setCameras(data ?? [])
    setLoading(false)
  }, [siteId])

  useEffect(() => { load() }, [load])

  const createCamera = useCallback(async (camera: {
    site_id: string
    name: string
    cam_number: number
    cam_ip: string
    port: number
    external_ip?: string
    rtsp_url: string
    stream_name: string
    rtsp_path?: string
    username?: string
    password?: string
  }) => {
    const { data, error } = await supabase
      .from('cameras')
      .insert(camera)
      .select()
      .single()
    if (error) throw error
    // Auto-sync DNS when external_ip is set
    if (camera.external_ip) {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
          const dnsResp = await fetch('/api/camera/dns-sync', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ cameraId: (data as Camera).id, externalIp: camera.external_ip }),
          })
          const raw = await dnsResp.text()
          console.log('[dns-sync] Status:', dnsResp.status, 'Body:', raw.slice(0, 500))
        }
      } catch (err) {
        console.warn('[dns-sync] Error:', err)
      }
    }
    logClientAudit({ action: 'create', entity_type: 'camera', entity_id: (data as Camera).id, entity_name: camera.name })
    await load()
    return data as Camera
  }, [load])

  const updateCamera = useCallback(async (id: string, updates: Partial<Pick<Camera, 'name' | 'cam_ip' | 'port' | 'external_ip' | 'rtsp_path' | 'username' | 'password' | 'latitude' | 'longitude'>>) => {
    const { error } = await supabase
      .from('cameras')
      .update(updates)
      .eq('id', id)
    if (error) throw error
    logClientAudit({ action: 'update', entity_type: 'camera', entity_id: id, details: updates })
    // Auto-sync DNS when external_ip is set
    if (updates.external_ip) {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
          const dnsResp = await fetch('/api/camera/dns-sync', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ cameraId: id, externalIp: updates.external_ip }),
          })
          const raw = await dnsResp.text()
          console.log('[dns-sync] Status:', dnsResp.status, 'Body:', raw.slice(0, 500))
        }
      } catch (err) {
        console.warn('[dns-sync] Error:', err)
      }
    }
    await load()
  }, [load])

  const deleteCamera = useCallback(async (id: string) => {
    const cam = cameras.find(c => c.id === id)
    const { error } = await supabase
      .from('cameras')
      .delete()
      .eq('id', id)
    if (error) throw error
    logClientAudit({ action: 'delete', entity_type: 'camera', entity_id: id, entity_name: cam?.name })
    await load()
  }, [load, cameras])

  const nextCamNumber = cameras.length > 0
    ? Math.max(...cameras.map(c => c.cam_number)) + 1
    : 1

  return { cameras, loading, reload: load, createCamera, updateCamera, deleteCamera, nextCamNumber }
}
